import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-full font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [font-size:var(--site-button-font-size)] [gap:var(--site-button-gap)] [&_svg]:pointer-events-none [&_svg]:h-[var(--site-button-icon-size)] [&_svg]:w-[var(--site-button-icon-size)] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--site-primary-fill)] text-[var(--site-primary-text)] shadow-[var(--site-shadow-button)] hover:bg-[var(--site-primary-fill-strong)] hover:-translate-y-0.5",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[var(--site-shadow-soft)] hover:bg-destructive/90",
        outline:
          "border border-[color:var(--site-border-mid)] bg-[var(--site-surface)] text-[var(--site-ink-strong)] shadow-[var(--site-shadow-soft)] hover:bg-[var(--site-surface-hover)]",
        secondary:
          "bg-[var(--site-accent-soft)] text-[var(--site-accent-text)] shadow-[var(--site-shadow-soft)] hover:bg-[color:rgba(8,145,178,0.16)]",
        ghost: "text-[var(--site-ink)] hover:bg-[var(--site-surface)] hover:text-[var(--site-ink-strong)]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[var(--site-control-height)] px-[var(--site-button-px)] py-2.5",
        sm: "min-h-11 h-[var(--site-control-height-sm)] px-[var(--site-button-px-sm)] [font-size:var(--site-button-font-size-sm)]",
        lg: "h-[var(--site-control-height-lg)] px-[var(--site-button-px-lg)]",
        icon: "h-[var(--site-control-icon-size)] w-[var(--site-control-icon-size)] rounded-[var(--site-input-radius)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
